import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Building2 } from 'lucide-react';

const loginSchema = z.object({
  email: z.string().email('Ungültige E-Mail-Adresse'),
  password: z.string().min(1, 'Passwort erforderlich'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const { signIn, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      await signIn(data.email, data.password);
      navigate('/');
    } catch {
      // Error handled in store
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden bg-background">
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-grid opacity-60" />

      {/* Ambient glow — top right */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: -150,
          right: -100,
          width: 600,
          height: 600,
          background:
            'radial-gradient(circle at center, rgba(59,130,246,0.18) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
      />
      {/* Ambient glow — bottom left */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: -120,
          left: -100,
          width: 500,
          height: 500,
          background:
            'radial-gradient(circle at center, rgba(37,99,235,0.12) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-[380px]">
        <div className="rounded-2xl border border-white/10 bg-card p-6 sm:p-8 shadow-2xl shadow-black/30">
          {/* Icon + heading */}
          <div className="flex flex-col items-center gap-3 mb-7">
            <div className="w-12 h-12 rounded-xl border border-blue-500/20 bg-blue-500/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-center">
              <h1 className="text-lg font-semibold">Willkommen zurück</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Melde dich an, um dein Portfolio zu verwalten
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 mb-5 p-3 rounded-xl bg-destructive/10 border border-destructive/20">
              <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground tracking-widest uppercase">
                E-Mail
              </Label>
              <Input
                type="email"
                placeholder="name@beispiel.de"
                {...register('email')}
                error={errors.email?.message}
                onFocus={() => clearError()}
                className="h-11 rounded-xl bg-white/5 border-white/10 placeholder:text-white/20 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground tracking-widest uppercase">
                  Passwort
                </Label>
                <Link
                  to="/auth/forgot-password"
                  className="text-xs text-blue-400/60 hover:text-blue-400 transition-colors"
                >
                  Vergessen?
                </Link>
              </div>
              <Input
                type="password"
                placeholder="••••••••"
                {...register('password')}
                error={errors.password?.message}
                onFocus={() => clearError()}
                className="h-11 rounded-xl bg-white/5 border-white/10 placeholder:text-white/20 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-500/35 transition-shadow"
              isLoading={isLoading}
            >
              Anmelden
            </Button>
          </form>

          {/* Register link */}
          <p className="text-center text-xs text-muted-foreground mt-6 pt-5 border-t border-white/10">
            Noch kein Konto?{' '}
            <Link
              to="/auth/register"
              className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
            >
              Registrieren
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
